import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Users, ShieldCheck, MonitorPlay, Sparkles, Target, TrendingUp,
  LayoutDashboard, Inbox, Banknote, Mail, Radar, Building2, Package,
  ClipboardCheck, Brain, GraduationCap, LineChart, Share2, Wrench, LogOut,
} from 'lucide-react';
import { useRpc } from '../../lib/queries';
import type { InboxItem } from '../../lib/classes';
import { LogoMark, Wordmark } from '../ui/Logo';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}
interface NavGroup { label: string; items: NavItem[] }

/** The six pages Jamie runs the business from, then everything else under General. */
export const NAV_GROUPS: NavGroup[] = [
  { label: 'Business', items: [
    { to: '/users',      label: 'Users',      icon: Users },
    { to: '/compliance', label: 'Compliance', icon: ShieldCheck },
    { to: '/sessions',   label: 'Sessions',   icon: MonitorPlay },
    { to: '/vyo',        label: 'Vyo',        icon: Sparkles },
    { to: '/outcomes',   label: 'Outcomes',   icon: Target },
    { to: '/growth',     label: 'Growth',     icon: TrendingUp },
  ]},
  { label: 'General', items: [
    { to: '/',                       label: 'Overview',          icon: LayoutDashboard },
    { to: '/inbox',                  label: 'Inbox',             icon: Inbox },
    { to: '/revenue',                label: 'Revenue',           icon: Banknote },
    { to: '/email',                  label: 'Email',             icon: Mail },
    { to: '/alerts',                 label: 'Radar',             icon: Radar },
    { to: '/catalogue',              label: 'Firms and tracker', icon: Building2 },
    { to: '/packs',                  label: 'Packs',             icon: Package },
    { to: '/psychometrics',          label: 'Psychometrics',     icon: ClipboardCheck },
    { to: '/psychometric/authoring', label: 'Item authoring',    icon: Brain },
    { to: '/learn',                  label: 'Learn',             icon: GraduationCap },
    { to: '/events',                 label: 'Events',            icon: LineChart },
    { to: '/referrals',              label: 'Referrals',         icon: Share2 },
    { to: '/ops',                    label: 'Ops',               icon: Wrench },
  ]},
];

const NavButton: React.FC<{ item: NavItem; badge?: React.ReactNode }> = ({ item, badge }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        [
          'flex items-center gap-2.5 px-2.5 h-8 rounded-md text-[13.5px] transition-colors',
          isActive ? 'bg-stone-100 text-stone-900 font-semibold' : 'text-stone-600 font-medium hover:bg-stone-50 hover:text-stone-900',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-indigo-600' : 'text-stone-400'}`} />
          <span className="truncate flex-1">{item.label}</span>
          {badge}
        </>
      )}
    </NavLink>
  );
};

export const Sidebar: React.FC<{ userEmail: string | null; onSignOut: () => void }> = ({ userEmail, onSignOut }) => {
  const { data: inbox } = useRpc<InboxItem[]>('admin_inbox', {}, { refetchInterval: 120_000 });
  const urgent = (inbox ?? []).filter(i => i.severity !== 'info');
  const hasAct = urgent.some(i => i.severity === 'act');

  const inboxBadge = urgent.length > 0 ? (
    <span className={[
      'text-[11px] font-semibold rounded px-1.5 tabular-nums',
      hasAct ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-800',
    ].join(' ')}>
      {urgent.length}
    </span>
  ) : undefined;

  return (
    <aside className="w-60 shrink-0 flex flex-col bg-white border-r border-stone-200 h-full">
      <div className="px-4 pt-5 pb-4 flex items-center gap-2.5">
        <LogoMark className="w-6 h-6" />
        <Wordmark />
      </div>

      <nav className="flex-1 px-3 pb-3 overflow-y-auto">
        {NAV_GROUPS.map(group => (
          <div key={group.label} className="mb-4">
            <div className="px-2.5 pb-1.5">
              <span className="text-[12px] font-medium text-stone-400">{group.label}</span>
            </div>
            <div className="space-y-px">
              {group.items.map(item => (
                <NavButton key={item.to} item={item} badge={item.to === '/inbox' ? inboxBadge : undefined} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-stone-200">
        <div className="px-2.5 pb-2 text-[12px] text-stone-500 truncate">{userEmail ?? 'Signed in'}</div>
        <button
          onClick={onSignOut}
          className="w-full flex items-center gap-2 px-2.5 h-8 rounded-md text-[13px] font-medium text-stone-600 hover:bg-stone-50 hover:text-stone-900 transition-colors"
        >
          <LogOut className="w-4 h-4 text-stone-400" /> Sign out
        </button>
      </div>
    </aside>
  );
};
